import React from 'react';
import { userStoreApi, useUserStore } from '@gik/core/store/UserStore';
import { useUser } from '@gik/api/users/user';
import { useInkind } from '@gik/api/inkinds/inkind';
import { UI } from '@gik/ui';
import * as authApi from '@gik/api/users/auth';
import { useEmailAlreadyTaken } from '@gik/api/users/auth';
import {
  acceptInvitation,
  useInvitationEmailAddressFromToken,
} from '@gik/finish-registration/api/FinishRegistrationAPI';
import { useSearchParam } from 'react-use';
import { finishRegistrationSearchParam, signupEmailSearchParam } from '@gik/finish-registration/consts';
import { FinishRegistration } from '@gik/finish-registration';
import { auth } from '@gik/auth';

export default function useFinishRegistration(routeId: string) {
  const userId = useUserStore(state => state.id);
  const { data: user } = useUser(userId);
  const { data: inkindPage, revalidate: revalidateInkindPage } = useInkind(routeId);

  const invitationToken = useSearchParam(finishRegistrationSearchParam);
  const signUpEmailAddress = useSearchParam(signupEmailSearchParam);

  const { data: { email: invitationEmailAddress } = { email: undefined } } = useInvitationEmailAddressFromToken(
    invitationToken
  );

  const { data: emailExistsStatus } = useEmailAlreadyTaken(invitationEmailAddress);
  const exist = emailExistsStatus?.exist || emailExistsStatus?.facebook || emailExistsStatus?.google;

  const buttonsPortal = React.useRef<HTMLDivElement>();
  const debounceRef = React.useRef<NodeJS.Timeout>();
  const timeoutValue = 600;

  function withDebounce(fn: () => void) {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fn();
    }, timeoutValue);
  }

  React.useEffect(() => {
    async function openFinishRegistrationModal() {
      await UI.dialog(<FinishRegistration routeId={routeId} buttonsPortal={buttonsPortal} />, {
        title: 'Finish Registration',
        closable: false,
        buttons: <div ref={buttonsPortal} />,
      });
    }

    async function alreadyAcceptedInvitationAlert() {
      await UI.alert(
        `Thank you, you have already finished registering.${user ? '' : ' Please log in to your account to continue.'}`,
        {
          title: signUpEmailAddress ? 'Already registered' : 'Invitation already accepted',
        }
      );

      const url = new URL(window.location.toString());
      url.searchParams.delete(finishRegistrationSearchParam);
      url.searchParams.delete(signupEmailSearchParam);
      window.history.pushState({}, '', url.toString());
    }

    async function openDifferentEmailLogOutConfirmDialog() {
      if (
        await UI.confirm(
          `You are accepting an invitation to register under email ${
            invitationEmailAddress ? invitationEmailAddress : '<>'
          } but are logged in with a different email (${
            user?.emailAddress || '<>'
          }). We will log you out so you can continue registering your new account.`,
          {
            title: 'Finish Registration',
          }
        )
      ) {
        await authApi.logout();
        userStoreApi.getState().logout();
      } else {
        const url = new URL(window.location.toString());
        url.searchParams.delete(finishRegistrationSearchParam);
        url.searchParams.delete(signupEmailSearchParam);
        window.history.pushState({}, '', url.toString());
      }
    }

    withDebounce(() => {
      if (inkindPage) {
        if (signUpEmailAddress) {
          if (user) {
            if (user.emailAddress.toLowerCase().localeCompare(invitationEmailAddress.toLowerCase()) === 0) {
              alreadyAcceptedInvitationAlert();
            } else {
              openDifferentEmailLogOutConfirmDialog();
            }
          } else if (!userId) {
            if (exist) {
              alreadyAcceptedInvitationAlert();
            } else {
              openFinishRegistrationModal();
            }
          }
        } else if (invitationToken) {
          try {
            if (user) {
              if (user.emailAddress.toLowerCase().localeCompare(invitationEmailAddress.toLowerCase()) === 0) {
                (async () => {
                  await acceptInvitation(inkindPage.routeId, invitationToken);

                  const url = new URL(window.location.toString());
                  url.searchParams.delete(finishRegistrationSearchParam);
                  // url.searchParams.delete(signupEmailSearchParam);
                  window.history.pushState({}, '', url.toString());
                  revalidateInkindPage();
                })();
              } else {
                openDifferentEmailLogOutConfirmDialog();
              }
            } else if (!userId && !exist) {
              openFinishRegistrationModal();
            } else {
              auth.login();
            }
          } catch (e) {
            console.error(e);
            alreadyAcceptedInvitationAlert();
          }
        }
      }
    });
  }, [
    inkindPage,
    user,
    userId,
    invitationToken,
    signUpEmailAddress,
    routeId,
    invitationEmailAddress,
    exist,
    revalidateInkindPage,
  ]);
}
